// ts-rest contract for the overdrawn-cash report (issue #44 / PRD #21).
//
// The overdrawn-cash surface is a snapshot-derived read view sourced from
// the `overdrawn_cash_accounts` file type under `scope='current'`
// (ADR-0008). It carries the same response envelope as every other
// snapshot-derived surface — rows wrapped with the `versionRef` they
// were served from (`@/schemas/version-ref`).
//
// Endpoints under `/api/cash/overdrawn`:
//   GET  /api/cash/overdrawn                  — read active or named snapshot
//   POST /api/cash/overdrawn/uploads          — upload a fresh CSV (multipart)
//
// The two row-level Actions (`AssignOverdraftOwner`, `ResolveOverdraftRow`)
// ship in issue #48 — deliberately out of scope here.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { snapshotResponse, SnapshotQuerySchema } from '../schemas/version-ref';

const c = initContract();

// ---------------------------------------------------------------------------
// Row shape — mirrors the `OdCashRow` Object Type (the snake_case persistence
// shape is what the read endpoint emits; no DTO renaming happens on the wire).
// ---------------------------------------------------------------------------

export const odCashRowSchema = z.object({
    id: z.string(),
    snapshotId: z.string(),
    // Position in the parsed file (0-indexed). Stable per upload — keys the
    // overdraft-resolution side table introduced in #48.
    rowIndex: z.number().int().nonnegative(),
    clientReference: z.string(),
    investmentManager: z.string(),
    accountReference: z.string(),
    cashAccountName: z.string(),
    currency: z.string(),
    postedBalance: z.number(),
    registration: z.string(),
    lastRecalculatedOn: z.string(),
    matchedUserId: z.string().nullable(),
    unresolvedImString: z.string().nullable(),
    // Derived display fields joined from the OverdraftResolution side table
    // on read (#48). The underlying OdCashRow stays immutable; these
    // attachments survive snapshot rollback by keying on
    // (snapshotId, rowIndex).
    ownerUserId: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    // Escalation state joined from the same side row (SEP-15).
    escalationNote: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    escalatedBy: z.string().nullable(),
    // Derived operator status: resolvedAt wins, then escalatedAt, else open.
    status: z.enum(['open', 'escalated', 'resolved']),
});
export type OdCashRowDto = z.infer<typeof odCashRowSchema>;

export const overdrawnCashResponseSchema = snapshotResponse(z.array(odCashRowSchema));
export type OverdrawnCashResponse = z.infer<typeof overdrawnCashResponseSchema>;

export const uploadOverdrawnCashResponseSchema = z.object({
    ok: z.literal(true),
    scope: z.literal('current'),
    snapshotId: z.string(),
    versionN: z.number().int(),
    rowCount: z.number().int(),
    // Per-outcome counts for the alias-resolution step run at parse time.
    // `resolved` is the count of rows whose `investmentManager` resolved to
    // exactly one user; `unresolved` carries every other row (zero matches,
    // multiple matches, or empty IM string).
    resolved: z.number().int(),
    unresolved: z.number().int(),
});
export type UploadOverdrawnCashResponse = z.infer<typeof uploadOverdrawnCashResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Issue #48 — overdrawn-cash row Actions (PRD #21).
//
// Two row-level Actions wired off the `/ops/cash/overdrawn` view. Both are
// keyed by `(snapshotId, rowIndex)` so the operator state persists across
// snapshot rollback (#31) — the underlying `OdCashRow` is immutable.
// ---------------------------------------------------------------------------

export const assignOverdraftOwnerRequestSchema = z.object({
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    ownerUserId: z.string().min(1),
    assignmentReason: z.string().min(1).optional(),
});
export type AssignOverdraftOwnerRequest = z.infer<typeof assignOverdraftOwnerRequestSchema>;

export const resolveOverdraftRowRequestSchema = z.object({
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    resolutionNote: z.string().min(1),
});
export type ResolveOverdraftRowRequest = z.infer<typeof resolveOverdraftRowRequestSchema>;

export const escalateOverdraftRowRequestSchema = z.object({
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    escalationNote: z.string().min(1),
});
export type EscalateOverdraftRowRequest = z.infer<typeof escalateOverdraftRowRequestSchema>;

export const overdraftActionResponseSchema = z.object({
    ok: z.literal(true),
});
export type OverdraftActionResponse = z.infer<typeof overdraftActionResponseSchema>;

// ---------------------------------------------------------------------------
// Cash Management — full cash-positions surface (sourced from the
// `cash_accounts` CashAccountsExport snapshot). Each position carries its
// derived threshold status and the Open CashAlert (if any) the per-row
// Acknowledge / Escalate actions target.
// ---------------------------------------------------------------------------

export const cashPositionStatusSchema = z.enum([
    'Normal',
    'Low Cash Alert',
    'Excess Cash',
    'Overdrawn',
]);

/** The Open CashAlert attached to a position (null when within normal bounds). */
export const cashPositionAlertSchema = z.object({
    id: z.string(),
    alertType: z.enum(['LowCash', 'Overdrawn', 'ExcessCash']),
    status: z.enum(['Open', 'Resolved']),
    severity: z.string(),
    acknowledgedAt: z.string().nullable(),
    escalationLevel: z.number().int(),
});
export type CashPositionAlertDto = z.infer<typeof cashPositionAlertSchema>;

export const cashPositionRowSchema = z.object({
    // The durable CashPosition id (deterministic from the cash-account ref) —
    // stable across re-uploads.
    id: z.string(),
    snapshotId: z.string(),
    rowIndex: z.number().int().nonnegative(),
    accountRef: z.string(),
    cashAccountName: z.string(),
    clientName: z.string(),
    clientReference: z.string(),
    investmentManager: z.string(),
    currency: z.string(),
    availableBalance: z.number(),
    postedBalance: z.number(),
    clearedBalance: z.number(),
    // Headline balance = posted balance (the surface decision).
    balance: z.number(),
    status: cashPositionStatusSchema,
    alert: cashPositionAlertSchema.nullable(),
});
export type CashPositionRowDto = z.infer<typeof cashPositionRowSchema>;

export const cashPositionsResponseSchema = snapshotResponse(z.array(cashPositionRowSchema)).extend({
    // Cause 2 — the Cash Management integrity banner reads these. A count-shaped
    // read of durable CashPositions and the active snapshot's rowCount let the UI
    // distinguish "snapshot has rows but positions are missing" (broken compute →
    // Recompute) from "empty active snapshot" (H1 → re-upload).
    materialisedPositions: z.number().int().nonnegative(),
    /**
     * Positions a COMPLETE materialisation of the active snapshot produces (its
     * distinct cash accounts). The banner compares against this rather than
     * testing `materialisedPositions === 0`: the repair commits in chunks, so an
     * abort part-way leaves a NON-zero partial count that a `> 0` test read as
     * health, hiding the affordance for good (SEP-184b).
     */
    expectedPositions: z.number().int().nonnegative(),
    activeSnapshotRowCount: z.number().int().nonnegative(),
});
export type CashPositionsResponse = z.infer<typeof cashPositionsResponseSchema>;

// ---------------------------------------------------------------------------
// Cash Management — operator "Recompute positions" repair (Cause 2 / B6).
// Replays materialisation + cash-alert detection against the ALREADY-ACTIVE
// cash_accounts snapshot. Empty body — the Action resolves the active pointer.
// ---------------------------------------------------------------------------

export const rematerialiseCashPositionsResponseSchema = z.object({
    snapshotId: z.string().uuid(),
    positionsMaterialised: z.number().int().nonnegative(),
    /** Rows merged because another row named the SAME cash account. */
    duplicatesCollapsed: z.number().int().nonnegative(),
    /**
     * SEP-184b — merged groups whose rows disagreed on the posted balance. A
     * benign repeat is normal; a disagreement means last-wins guessed, so the
     * operator is told rather than left with a silently discarded balance.
     */
    balanceConflicts: z.number().int().nonnegative(),
    alertsRaised: z.number().int().nonnegative(),
    alertsResolved: z.number().int().nonnegative(),
});
export type RematerialiseCashPositionsResponse = z.infer<
    typeof rematerialiseCashPositionsResponseSchema
>;

export const uploadCashAccountsResponseSchema = z.object({
    ok: z.literal(true),
    scope: z.literal('current'),
    snapshotId: z.string(),
    versionN: z.number().int(),
    // What the parse produced.
    rowCount: z.number().int(),
    // What this version actually persisted — 0 for a duplicate re-upload, which
    // mints the version but writes no rows (SEP-184a). Reported alongside
    // `rowCount` so the upload drawer cannot render "35 rows" for an upload that
    // stored nothing and left the surface stale.
    materialisedRowCount: z.number().int().nonnegative().optional(),
    duplicateOfPrevious: z.boolean().optional(),
    // Post-commit downstream compute outcome.
    positionsMaterialised: z.number().int(),
    alertsRaised: z.number().int(),
    alertsResolved: z.number().int(),
    // Number of rows merged during materialisation because another row named the
    // SAME cash account — the export lists one account once per portfolio
    // association. A real domain signal, surfaced in the upload toast.
    duplicatesCollapsed: z.number().int().nonnegative().optional(),
    // SEP-184b — merged groups whose rows disagreed on the posted balance, so
    // last-wins discarded a real figure. Carried on the UPLOAD response too, not
    // just the Recompute one: the upload is the door operators actually use.
    balanceConflicts: z.number().int().nonnegative().optional(),
    // The snapshot committed (200) but the post-commit compute failed. The
    // operator repairs via the Cash Management banner. `computeSummary` is
    // SANITISED — never the raw Postgres detail.
    computeFailed: z.boolean().optional(),
    computeErrorCode: z.string().optional(),
    computeSummary: z.string().optional(),
});
export type UploadCashAccountsResponse = z.infer<typeof uploadCashAccountsResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const cashContract = c.router(
    {
        /**
         * GET /api/cash/overdrawn
         *
         * Defaults to the active version named by the `(overdrawn_cash_accounts,
         * current)` `SnapshotPointer`. Pass `?version=<ParsedSnapshot.id>` to
         * read a specific historical version. The response wraps rows together
         * with the `versionRef` it resolved.
         *
         * When no upload has ever happened (no pointer), the response returns
         * an empty `rows` array and `versionRef.versionId: null` — "no data
         * yet, honestly", not 404.
         */
        listOverdrawn: {
            method: 'GET',
            path: '/cash/overdrawn',
            query: SnapshotQuerySchema,
            responses: {
                200: overdrawnCashResponseSchema,
                404: errorBody,
            },
            summary: 'List active-or-named-snapshot overdrawn-cash rows (ADR-0008).',
        },
        /**
         * GET /api/desk/cash/overdrawn
         *
         * The Desk (IM) projection of the same `(overdrawn_cash_accounts,
         * current)` snapshot as `listOverdrawn`, but scoped server-side to the
         * signed-in IM's own book: an IM on the `desk` persona sees only rows
         * whose resolved owner/IM is them (the `DeskScope` — the Desk scoping
         * the rest of the Desk surfaces use). Read-only; the assign/resolve
         * Actions stay on the Ops surface. Same response envelope + empty-state
         * semantics as `listOverdrawn`.
         */
        listOverdrawnForDesk: {
            method: 'GET',
            path: '/desk/cash/overdrawn',
            query: SnapshotQuerySchema,
            responses: {
                200: overdrawnCashResponseSchema,
                404: errorBody,
            },
            summary: "List the signed-in IM's own overdrawn-cash rows (Desk, IM-scoped).",
        },
        /**
         * POST /api/cash/overdrawn/assign — issue #48 / PRD #21.
         * Dispatches `AssignOverdraftOwner`. Returns 404 when `ownerUserId`
         * does not exist in the global users directory.
         */
        assignOverdraftOwner: {
            method: 'POST',
            path: '/cash/overdrawn/assign',
            body: assignOverdraftOwnerRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
                404: errorBody,
            },
            summary: 'Assign an owner User to an overdrawn-cash row (registry-dispatched).',
        },
        /**
         * POST /api/cash/overdrawn/resolve — issue #48 / PRD #21.
         * Dispatches `ResolveOverdraftRow`. Writes to the OverdraftResolution
         * side table; the underlying OdCashRow stays immutable.
         */
        resolveOverdraftRow: {
            method: 'POST',
            path: '/cash/overdrawn/resolve',
            body: resolveOverdraftRowRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
            },
            summary: 'Record a resolution note on an overdrawn-cash row (registry-dispatched).',
        },
        /**
         * POST /api/cash/overdrawn/escalate — SEP-15.
         * Dispatches `EscalateOverdraftRow`. Writes the escalation triplet to
         * the OverdraftResolution side table; the underlying OdCashRow stays
         * immutable. Rejects with 400 ALREADY_RESOLVED when the row already
         * carries a resolution.
         */
        escalateOverdraftRow: {
            method: 'POST',
            path: '/cash/overdrawn/escalate',
            body: escalateOverdraftRowRequestSchema,
            responses: {
                200: overdraftActionResponseSchema,
                400: errorBody,
            },
            summary: 'Escalate an overdrawn-cash row to Compliance (registry-dispatched).',
        },
        /**
         * GET /api/comply/cash/overdrawn — SEP-15.
         *
         * The Comply projection of the same `(overdrawn_cash_accounts, current)`
         * snapshot as `listOverdrawn`, filtered server-side to rows whose
         * derived status is 'escalated' (escalated and not yet resolved).
         * Read-only for Compliance — resolving is an Ops act and drops the row
         * off this list. Same response envelope + empty-state semantics as
         * `listOverdrawn`.
         */
        listOverdrawnEscalationsForComply: {
            method: 'GET',
            path: '/comply/cash/overdrawn',
            query: SnapshotQuerySchema,
            responses: {
                200: overdrawnCashResponseSchema,
                404: errorBody,
            },
            summary: 'List escalated overdrawn-cash rows for the Comply Dashboard (SEP-15).',
        },
        /**
         * GET /api/cash/positions
         *
         * Active-or-named-snapshot cash positions for Cash Management, sourced
         * from the `(cash_accounts, current)` snapshot. Each row carries its
         * derived threshold `status` and the Open `CashAlert` (or null) that the
         * Acknowledge / Escalate actions target. Empty `rows` + `versionId: null`
         * when nothing has been uploaded yet (not a 404).
         */
        listCashPositions: {
            method: 'GET',
            path: '/cash/positions',
            query: SnapshotQuerySchema,
            responses: {
                200: cashPositionsResponseSchema,
                404: errorBody,
            },
            summary:
                'List active-or-named-snapshot cash positions with their open alerts (ADR-0008).',
        },
        /**
         * POST /api/cash/positions/rematerialise — Cause 2 / B6.
         *
         * Operator "Recompute positions" repair. Empty body: the underlying
         * `RematerialiseCashPositions` Action resolves the active
         * `(cash_accounts, current)` pointer itself, refuses a missing pointer or
         * a zero-row target (400), and otherwise replays materialisation +
         * cash-alert detection. The position upsert is idempotent (deterministic
         * cashPositionId). Concurrency is guarded server-side by a Postgres
         * advisory lock, so a second concurrent run BLOCKS until the first
         * finishes and then no-ops on the net-new alert set — but a timed-out
         * request is NOT a signal to blindly retry: the run is likely still in
         * progress. Verify completion via the materialised position count, never
         * the HTTP response, before re-invoking.
         */
        rematerialiseCashPositions: {
            method: 'POST',
            path: '/cash/positions/rematerialise',
            body: z.object({}),
            responses: {
                200: rematerialiseCashPositionsResponseSchema,
                400: errorBody,
            },
            summary: 'Recompute cash positions from the active snapshot (Cause 2 repair).',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
